/**
 * API pública del kit de celebraciones.
 *
 *   const handle = play('confetti-cannons', { target: el });
 *   handle.stop(); // cancelar antes de tiempo
 *
 * `play()` resuelve el efecto por id, respeta `prefers-reduced-motion` (cae a
 * `sober()` —o a la variante sobria propia del efecto, ver `REDUCED_VARIANT`—
 * salvo en `achievement`/`success-pulse`, que ya son sobrios), y
 * nunca tira: ante un id desconocido o un error de efecto, loguea y devuelve
 * un handle no-op. La política fail-fast del repo aplica a config/billing; un
 * efecto visual roto NO debe tumbar la conversación del alumno.
 */

import type { CelebrationHandle, CelebrationOptions, CelebrationType, EffectFn } from './types.js';
import {
  achievement,
  bonusRain,
  bonusSober,
  confettiCannons,
  confettiRain,
  emojiFloat,
  fireworks,
  levelUp,
  sober,
  sparkles,
  streak,
  successPulse,
} from './effects.js';

const EFFECTS: Record<CelebrationType, EffectFn> = {
  'confetti-cannons': confettiCannons,
  'confetti-rain': confettiRain,
  fireworks,
  sparkles,
  streak,
  'level-up': levelUp,
  'emoji-float': emojiFloat,
  achievement,
  'success-pulse': successPulse,
  'bonus-rain': bonusRain,
};

const NOOP: CelebrationHandle = { stop: () => undefined };

/** Efectos que ya son sobrios → no se sustituyen bajo reduced-motion. */
const SOBER_EXEMPT: ReadonlySet<CelebrationType> = new Set(['achievement', 'success-pulse']);

/**
 * Variante sobria PROPIA de un efecto bajo reduced-motion. El `sober()` genérico
 * (un tinte de 1 s) se queda corto para el festejo bonus: el alumno tiene que
 * enterarse de que cumplió el bonus aunque no haya movimiento. Los que no están
 * acá caen al `sober()` genérico.
 */
const REDUCED_VARIANT: Partial<Record<CelebrationType, EffectFn>> = {
  'bonus-rain': bonusSober,
};

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function play(type: CelebrationType, options?: CelebrationOptions): CelebrationHandle {
  const opts = options ?? {};
  const fn = EFFECTS[type];
  if (!fn) {
    console.warn('[celebrations] efecto desconocido:', type);
    return NOOP;
  }
  const reduced = prefersReducedMotion() && !SOBER_EXEMPT.has(type);
  const impl = reduced ? (REDUCED_VARIANT[type] ?? sober) : fn;
  try {
    return impl(opts);
  } catch (e) {
    console.error('[celebrations] error en', type, e);
    return NOOP;
  }
}

export { CELEBRATION_CATALOG, BRAND, WARM, GOLD } from './catalog.js';
export type {
  CelebrationType,
  CelebrationOptions,
  CelebrationHandle,
  CatalogEntry,
} from './types.js';
